import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCopyToClipboard } from "../../../hooks/useCopyToClipboard";
import { useModalDialog } from "../../../hooks/useModalDialog";
import { storyItemSqlKey } from "../../../hooks/useStoryItemSql";
import { fetchStoryItemSql } from "../../../services/storyCanvas/storyItemSqlApi";
import { buildAllSqlFile, sqlFileName, type StorySqlFileEntry } from "./storySqlSample";
import type { StorySqlChart, StorySqlLayout } from "./StorySqlDialog.types";
import type { StoryPageVm } from "../../../services/storyCanvas/storyPages.types";
import { canGroupBy, groupStorySqlCharts, type StorySqlGroupMode } from "../../../services/storyCanvas/storySqlGroups";

/** Charts whose title contains the query, case-insensitive; a blank query keeps all. */
export const filterCharts = (
  charts: readonly StorySqlChart[],
  query: string,
): StorySqlChart[] => {
  const needle = query.trim().toLowerCase();
  return needle
    ? charts.filter((chart) => chart.label.toLowerCase().includes(needle))
    : [...charts];
};

/** Adds or removes an id from the compared pair; at most two, the oldest drops first. */
export const toggleCompared = (ids: readonly string[], id: string): string[] =>
  ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id].slice(-2);

/** The dialog's state: the picked chart, search, compare mode and copy feedback. */
export const useStorySqlDialogInternalState = (
  storyId: string,
  storyTitle: string,
  open: boolean,
  charts: readonly StorySqlChart[],
  initialChartId: string | null | undefined,
  onClose: () => void,
  pages: readonly StoryPageVm[] = [],
  untaggedTitle = "",
) => {
  const [groupMode, setGroupMode] = useState<StorySqlGroupMode>("all");
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [isComparing, setIsComparing] = useState(false);
  const [comparedIds, setComparedIds] = useState<string[]>([]);
  const [layout, setLayout] = useState<StorySqlLayout>("readable");
  // The dialog stays mounted while it is closed, so every open starts clean: a chart picked, a search or
  // compare mode left from the last open must not beat the chart this open was asked for.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) {
      setPickedId(null);
      setQuery("");
      setIsComparing(false);
      setComparedIds([]);
      setLayout("readable");
      setGroupMode("all");
    }
  }
  const { copiedKey, copy } = useCopyToClipboard();
  const dialog = useModalDialog(open, onClose);
  const queryClient = useQueryClient();
  const [downloadState, setDownloadState] = useState<"idle" | "busy" | "error">("idle");

  // PENDING: PENDING-DOWNLOAD-ALL-SQL (LOCAL: reads each chart's SQL like the panes do, saves one file)
  const downloadAll = useCallback(async () => {
    setDownloadState("busy");
    try {
      const entries: StorySqlFileEntry[] = await Promise.all(
        charts.map(async (chart) => {
          try {
            const data = await queryClient.fetchQuery({
              queryKey: storyItemSqlKey(storyId, chart.id),
              queryFn: () => fetchStoryItemSql(storyId, chart.id),
              staleTime: 60_000,
            });
            return { title: chart.label, statements: data.statements, message: data.message };
          } catch {
            return { title: chart.label, statements: [], message: "SQL could not be read" };
          }
        }),
      );
      const blob = new Blob([buildAllSqlFile(storyTitle, entries)], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = sqlFileName(storyTitle);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setDownloadState("idle");
    } catch {
      setDownloadState("error");
    }
  }, [charts, queryClient, storyId, storyTitle]);

  const fallbackId = charts.some((c) => c.id === initialChartId)
    ? (initialChartId as string)
    : (charts[0]?.id ?? null);
  const activeId = charts.some((c) => c.id === pickedId)
    ? pickedId
    : fallbackId;
  const visible = useMemo(() => filterCharts(charts, query), [charts, query]);
  const canGroupByPage = canGroupBy("page", pages, charts);
  const canGroupByTag = canGroupBy("tag", pages, charts);
  const shownMode: StorySqlGroupMode =
    (groupMode === "page" && !canGroupByPage) || (groupMode === "tag" && !canGroupByTag) ? "all" : groupMode;
  const groups = useMemo(
    () => groupStorySqlCharts(visible, shownMode, pages, untaggedTitle),
    [visible, shownMode, pages, untaggedTitle],
  );

  const toggleCompare = useCallback(() => {
    setIsComparing((on) => !on);
    setComparedIds([]);
  }, []);
  const pick = useCallback(
    (id: string) =>
      isComparing
        ? setComparedIds((ids) => toggleCompared(ids, id))
        : setPickedId(id),
    [isComparing],
  );
  const shownIds =
    isComparing && comparedIds.length > 0
      ? comparedIds
      : activeId
        ? [activeId]
        : [];

  return {
    dialog,
    query,
    setQuery,
    layout,
    setLayout,
    visible,
    groups,
    groupMode: shownMode,
    setGroupMode,
    canGroupByPage,
    canGroupByTag,
    activeId,
    isComparing,
    toggleCompare,
    comparedIds,
    pick,
    shownIds,
    downloadState,
    downloadAll,
    copiedKey,
    onCopy: (sql: string, key: string) => void copy(sql, key),
  };
};
